import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { pentestApi } from '../services/api';
import {
  Box, Button, Card, CardContent, Typography, Tabs, Tab, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert,
  CircularProgress, Grid
} from '@mui/material';
import {
  ArrowBack, Download, Psychology, Edit, Visibility, VisibilityOff,
  Block as BlockIcon
} from '@mui/icons-material';
import VoiceInput from '../components/VoiceInput';

const PenetrationTestDetails = () => {
  const { pentestId } = useParams();
  const navigate = useNavigate();
  const [pentest, setPentest] = useState(null);
  const [findings, setFindings] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [showIgnored, setShowIgnored] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [ignoreDialogOpen, setIgnoreDialogOpen] = useState(false);
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [ignoreReason, setIgnoreReason] = useState('');
  const [editingSummary, setEditingSummary] = useState(false);
  const [editedSummary, setEditedSummary] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Refs for voice input cursor positioning
  const ignoreReasonRef = useRef(null);
  const summaryRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [pentestId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [pentestData, findingsData] = await Promise.all([
        pentestApi.getById(pentestId).then(res => res.data),
        pentestApi.getFindings(pentestId, showIgnored).then(res => res.data)
      ]);
      
      setPentest(pentestData);
      setFindings(Array.isArray(findingsData) ? findingsData : []);
      setAnalysis(pentestData.analysis || null);
      
      if (pentestData.analysis) {
        setEditedSummary(pentestData.analysis.executive_summary || '');
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load penetration test details');
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async () => {
    try {
      setAnalyzing(true);
      setError(null);
      const result = await pentestApi.analyze(pentestId);
      setAnalysis(result);
      setEditedSummary(result.executive_summary || '');
      setSuccess('AI analysis completed successfully!');
      fetchData();
    } catch (err) {
      setError('Failed to generate AI analysis: ' + (err.message || 'Unknown error'));
    } finally {
      setAnalyzing(false);
    }
  };

  const handleGenerateReport = async () => {
    try {
      setGenerating(true);
      setError(null);
      await pentestApi.generateReport(pentestId);
      setSuccess('CISO report generated and downloaded successfully!');
    } catch (err) {
      setError('Failed to generate report: ' + (err.message || 'Unknown error'));
    } finally {
      setGenerating(false);
    }
  };

  const handleIgnoreFinding = async () => {
    if (!ignoreReason.trim()) {
      setError('Please provide a reason for ignoring this finding');
      return;
    }

    try {
      await pentestApi.ignoreFinding(selectedFinding.id, ignoreReason);
      setSuccess('Finding ignored successfully');
      setIgnoreDialogOpen(false);
      setIgnoreReason('');
      setSelectedFinding(null);
      fetchData();
    } catch (err) {
      setError('Failed to ignore finding');
    }
  };

  const handleUnignoreFinding = async (findingId) => {
    try {
      await pentestApi.unignoreFinding(findingId);
      setSuccess('Finding restored successfully');
      fetchData();
    } catch (err) {
      setError('Failed to restore finding');
    }
  };

  const handleSaveSummary = async () => {
    try {
      await pentestApi.updateCisoSummary(pentestId, editedSummary);
      setSuccess('Executive summary updated successfully');
      setEditingSummary(false);
      fetchData();
    } catch (err) {
      setError('Failed to update summary');
    }
  };

  const getSeverityColor = (severity) => {
    const colors = { 'Critical': 'error', 'High': 'warning', 'Medium': 'info', 'Low': 'success', 'Info': 'default' };
    return colors[severity] || 'default';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!pentest) {
    return <Alert severity="error">Penetration test not found</Alert>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/penetration-tests')}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4">{pentest.test_name}</Typography>
        </Box>
        <Box display="flex" gap={2}>
          {!analysis && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={analyzing ? <CircularProgress size={20} /> : <Psychology />}
              onClick={handleAnalyze}
              disabled={analyzing}
            >
              {analyzing ? 'Analyzing...' : 'Run AI Analysis'}
            </Button>
          )}
          {analysis && (
            <Button
              variant="contained"
              color="primary"
              startIcon={generating ? <CircularProgress size={20} /> : <Download />}
              onClick={handleGenerateReport}
              disabled={generating}
            >
              {generating ? 'Generating...' : 'Generate CISO Report'}
            </Button>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}

      <Card>
        <CardContent>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
            <Tab label="Overview" />
            <Tab label="Findings" />
            {analysis && <Tab label="AI Analysis" />}
          </Tabs>

          {/* Overview Tab */}
          {activeTab === 0 && (
            <Box sx={{ pt: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Test Information</Typography>
                  <Typography><strong>Client:</strong> {pentest.client_name}</Typography>
                  <Typography><strong>Test Type:</strong> {pentest.test_type}</Typography>
                  <Typography><strong>Test Date:</strong> {new Date(pentest.test_date).toLocaleDateString()}</Typography>
                  <Typography><strong>Status:</strong> <Chip label={pentest.status} size="small" /></Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Findings Summary</Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {pentest.critical_count > 0 && <Chip label={`Critical: ${pentest.critical_count}`} color="error" />}
                    {pentest.high_count > 0 && <Chip label={`High: ${pentest.high_count}`} color="warning" />}
                    {pentest.medium_count > 0 && <Chip label={`Medium: ${pentest.medium_count}`} color="info" />}
                    {pentest.low_count > 0 && <Chip label={`Low: ${pentest.low_count}`} color="success" />}
                    {pentest.info_count > 0 && <Chip label={`Info: ${pentest.info_count}`} color="default" />}
                  </Box>
                  <Typography sx={{ mt: 2 }}><strong>Total Findings:</strong> {pentest.total_findings}</Typography>
                  <Typography><strong>Risk Score:</strong> {pentest.overall_risk_score}/100</Typography>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Findings Tab */}
          {activeTab === 1 && (
            <Box sx={{ pt: 3 }}>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography variant="h6">Findings ({findings.length})</Typography>
                <Button
                  startIcon={showIgnored ? <Visibility /> : <VisibilityOff />}
                  onClick={() => {
                    setShowIgnored(!showIgnored);
                    pentestApi.getFindings(pentestId, !showIgnored).then(res => setFindings(res.data));
                  }}
                >
                  {showIgnored ? 'Hide' : 'Show'} Ignored
                </Button>
              </Box>
              
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Finding</strong></TableCell>
                      <TableCell><strong>Severity</strong></TableCell>
                      <TableCell><strong>Affected Hosts</strong></TableCell>
                      <TableCell><strong>Risk</strong></TableCell>
                      <TableCell align="center"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {findings.map((finding) => (
                      <TableRow key={finding.id} sx={{ opacity: finding.is_ignored ? 0.5 : 1 }}>
                        <TableCell>{finding.finding_name}</TableCell>
                        <TableCell>
                          <Chip label={finding.severity} color={getSeverityColor(finding.severity)} size="small" />
                        </TableCell>
                        <TableCell>{finding.affected_hosts || 'N/A'}</TableCell>
                        <TableCell>{finding.risk_explanation || 'N/A'}</TableCell>
                        <TableCell align="center">
                          {finding.is_ignored ? (
                            <Button size="small" onClick={() => handleUnignoreFinding(finding.id)}>
                              Restore
                            </Button>
                          ) : (
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedFinding(finding);
                                setIgnoreDialogOpen(true);
                              }}
                            >
                              <BlockIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* AI Analysis Tab */}
          {activeTab === 2 && analysis && (
            <Box sx={{ pt: 3 }}>
              <Typography variant="h6" gutterBottom>Executive Summary</Typography>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="body2" color="textSecondary">
                  {analysis.executive_summary_edited ? '(Edited by CISO)' : ''}
                </Typography>
                <Button
                  startIcon={<Edit />}
                  onClick={() => setEditingSummary(!editingSummary)}
                  size="small"
                >
                  {editingSummary ? 'Cancel' : 'Edit'}
                </Button>
              </Box>
              
              {editingSummary ? (
                <Box>
                  <TextField
                    multiline
                    rows={6}
                    fullWidth
                    value={editedSummary}
                    onChange={(e) => setEditedSummary(e.target.value)}
                    inputRef={summaryRef}
                    InputProps={{
                      endAdornment: (
                        <VoiceInput
                          onTranscript={(text) => setEditedSummary(text)}
                          currentValue={editedSummary}
                          inputRef={summaryRef}
                        />
                      )
                    }}
                  />
                  <Button variant="contained" onClick={handleSaveSummary} sx={{ mt: 2 }}>
                    Save Changes
                  </Button>
                </Box>
              ) : (
                <Typography paragraph>{analysis.executive_summary}</Typography>
              )}

              <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>Critical Remediation Priorities</Typography>
              {analysis.critical_priorities && analysis.critical_priorities.map((priority, idx) => (
                <Box key={idx} sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderLeft: 3, borderColor: 'primary.main' }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {priority.rank}. {priority.title}
                  </Typography>
                  <Typography paragraph>{priority.rationale}</Typography>
                  {priority.remediation && (
                    <Typography variant="body2" color="textSecondary">
                      <strong>Action:</strong> {priority.remediation}
                    </Typography>
                  )}
                </Box>
              ))}

              <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>Positive Security Observations</Typography>
              {analysis.positive_observations && (
                <ul>
                  {analysis.positive_observations.map((obs, idx) => (
                    <li key={idx}><Typography>{obs}</Typography></li>
                  ))}
                </ul>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Ignore Dialog */}
      <Dialog open={ignoreDialogOpen} onClose={() => setIgnoreDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ignore Finding</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Finding: {selectedFinding?.finding_name}
          </Typography>
          <TextField
            label="Reason for ignoring"
            multiline
            rows={4}
            fullWidth
            value={ignoreReason}
            onChange={(e) => setIgnoreReason(e.target.value)}
            sx={{ mt: 2 }}
            placeholder="Explain why this finding should be ignored..."
            inputRef={ignoreReasonRef}
            InputProps={{
              endAdornment: (
                <VoiceInput
                  onTranscript={(text) => setIgnoreReason(text)}
                  currentValue={ignoreReason}
                  inputRef={ignoreReasonRef}
                />
              )
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIgnoreDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleIgnoreFinding} variant="contained">Ignore Finding</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PenetrationTestDetails;
